/* plp-toolbar.js — the PLP toolbar: sort field, sort direction, page size,
 * view mode. An ADAPTER over the controls the page already has, not a second
 * engine.
 *
 * Why an adapter. The PLP has two rendering paths and they do not share a sort
 * vocabulary: the Typesense island refines through a connectSortBy widget
 * whose values are index names (`<collection>/sort/price:asc`, written to the
 * URL as `s=`), while the server-rendered fallback is a native <select> of
 * Shopify's own values (`price-ascending`, navigated as `sort_by=` by
 * section-render.js). Both already round-trip through the URL and both already
 * work. So this file reads whichever control is present, groups its options by
 * FIELD, remembers each field's ascending/descending twin, and applies a
 * choice by driving that original control. Neither engine was touched, no new
 * sort value exists, and old shared links keep working.
 *
 * State, and why it lives where it does:
 *   sort field + direction — the EXISTING url params (s= / sort_by=). No new
 *     vocabulary, nothing new for crawlers to find.
 *   view mode — localStorage ONLY. It is purely presentational (same products,
 *     same order, same page), so a URL param would mint a duplicate-content
 *     twin of every collection URL and halve the hit rate of the adjacent-page
 *     prefetch in page-prefetch.js. layout/theme.liquid stamps the stored mode
 *     onto <html> before first paint, so there is no grid flash and no
 *     load-time shift.
 *   page size — localStorage ONLY, applied as hitsPerPage on the live query.
 *     Never a URL param, for the same prefetch reason. Typesense-only, because
 *     Liquid cannot read the query string and so `paginate by` can never be
 *     driven by one (see snippets/plp-toolbar.liquid).
 */
(() => {
  'use strict';

  // The universal search can turn ANY page into a PLP after load
  // (usearch.js pushes /search and typesense-plp.js builds a shell), so this
  // file can no longer assume the toolbar exists at script-evaluation time.
  // It initialises on demand instead. Nothing is registered on the early
  // return, so a page that never grows a toolbar wires up nothing at all, and
  // the __plpInit flag stops a second pass on a page that already adopted one.
  document.addEventListener('plp:shell', function () { init(); });
  init();

  function init() {
  var bar = document.querySelector('[data-plp-toolbar]');
  if (!bar || bar.__plpInit) return;
  bar.__plpInit = true;

  var LS_VIEW = 'st.plp.view';
  var LS_PER = 'st.plp.per';
  var PER_OPTIONS = [24, 48, 72];
  var store = {
    get: function (k, d) { try { return localStorage.getItem(k) || d; } catch (e) { return d; } },
    set: function (k, v) { try { localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  /* ---------- sort: decompose whatever vocabulary is on the page ---------- */

  // Island values look like `<collection>/sort/price:asc` (or the bare
  // collection for the default); Shopify's look like `price-ascending`.
  function decompose(raw) {
    var v = String(raw == null ? '' : raw);
    var tail = v.indexOf('/sort/') > -1 ? v.split('/sort/')[1] : (/:(asc|desc)$/.test(v) ? v : null);
    if (tail) { var p = tail.split(':'); return { field: p[0], dir: p[1] || null }; }
    var m = v.match(/^(.*)-(ascending|descending)$/);
    if (m) return { field: m[1], dir: m[2] === 'ascending' ? 'asc' : 'desc' };
    return { field: v || 'featured', dir: null };
  }

  var FIELD_LABEL = {
    '': 'Featured', featured: 'Featured', manual: 'Featured', relevance: 'Relevance',
    price: 'Price', title: 'Name', created: 'Date added', created_at: 'Date added',
    'best-selling': 'Best selling', best_selling_rank: 'Best selling',
  };
  // fields whose ascending order is the one nobody means
  var DEFAULT_DESC = { created: 1, created_at: 1, 'best-selling': 1, best_selling_rank: 1 };
  var fieldLabel = function (f) {
    return FIELD_LABEL[f] || f.replace(/[-_]+/g, ' ').replace(/^./, function (c) { return c.toUpperCase(); });
  };

  // The source control, in whichever form this page has it.
  function readSource() {
    var sel = bar.querySelector('select[data-sort-select]');
    if (sel) {
      return {
        kind: 'select', el: sel,
        norm: function (v) { return v; },
        options: Array.prototype.map.call(sel.options, function (o) { return { value: o.value, label: o.textContent.trim() }; }),
        current: sel.value,
        apply: function (v) { sel.value = v; sel.dispatchEvent(new Event('change', { bubbles: true })); },
      };
    }
    var panel = bar.querySelector('[data-sort-combo] .pcombo__panel');
    if (!panel) return null;
    var opts = Array.prototype.slice.call(panel.querySelectorAll('[data-sort-value]'));
    if (!opts.length) return null;
    var active = panel.querySelector('.pcombo__opt.is-active') || opts[0];
    return {
      kind: 'combo', el: panel,
      // the island's DEFAULT option is the bare index name (the collection
      // handle), not a field:direction pair — decompose() would otherwise read
      // a sort field named after the collection. Normalise it to empty, which
      // FIELD_LABEL already maps to "Featured".
      norm: function (v) { return String(v || '').indexOf('/sort/') > -1 ? v : ''; },
      options: opts.map(function (o) { return { value: o.dataset.sortValue, label: (o.textContent || '').trim() }; }),
      current: active.dataset.sortValue,
      // clicking the widget's own option keeps InstantSearch in charge of the
      // refine, the URL write and the re-render — nothing is reimplemented
      apply: function (v) {
        var t = panel.querySelector('[data-sort-value="' + v.replace(/"/g, '\\"') + '"]');
        if (t) t.click();
      },
    };
  }

  var sortUI = bar.querySelector('[data-plp-sort-ui]');
  var sortCombo = bar.querySelector('[data-plp-sort-combo]');
  var sortPanel = sortCombo && sortCombo.querySelector('.pcombo__panel');
  var sortBtn = sortCombo && sortCombo.querySelector('.pcombo__btn');
  var sortCurrent = bar.querySelector('[data-plp-sort-current]');
  var dirBtn = bar.querySelector('[data-plp-dir]');
  var source = null;
  var fields = [];      // [{ field, label, asc, desc }]
  var sortSig = null;   // field list currently rendered into the panel

  function syncSort() {
    source = readSource();
    if (!source) { if (sortUI) sortUI.hidden = true; return; }
    var byField = {};
    var order = [];
    source.options.forEach(function (o) {
      var d = decompose(source.norm(o.value));
      if (!byField[d.field]) { byField[d.field] = { field: d.field, label: fieldLabel(d.field), asc: null, desc: null }; order.push(d.field); }
      if (d.dir === 'desc') byField[d.field].desc = o.value;
      else byField[d.field].asc = o.value;   // no direction at all counts as the field's only value
    });
    fields = order.map(function (f) { return byField[f]; });

    var cur = decompose(source.norm(source.current));
    var curField = byField[cur.field] || fields[0];
    if (sortCurrent) sortCurrent.textContent = curField ? curField.label : '';
    if (sortPanel) {
      // Rebuilding this panel on every island re-render is what made sort
      // clicks unreliable. The island re-renders ~4 times over ~750ms after any
      // change, and a click whose mousedown and mouseup straddle a rebuild fires
      // against a node that is no longer in the document, so the delegated
      // handler never sees it and the click silently does nothing. Measured on
      // the live preview theme: an option held for 300ms — an ordinary press —
      // is detached before mouseup. Rebuild ONLY when the field list itself
      // changes (rare); the active marker is updated in place.
      var sig = fields.map(function (f) { return f.field; }).join('|');
      if (sig !== sortSig) {
      sortSig = sig;
      sortPanel.innerHTML = fields.map(function (f) {
        var on = curField && f.field === curField.field;
        return '<button type="button" class="pcombo__opt' + (on ? ' is-active' : '') + '" role="option"'
          + ' aria-selected="' + (on ? 'true' : 'false') + '" data-plp-field="' + f.field + '">'
          + '<span class="pcombo__name"></span>'
          + '<span class="pcombo__tick" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span></button>';
      }).join('');
      // labels as TEXT, never as interpolated markup
      Array.prototype.forEach.call(sortPanel.querySelectorAll('.pcombo__opt'), function (b, i) {
        b.querySelector('.pcombo__name').textContent = fields[i].label;
      });
      }
      // active marker in place, so the nodes the user is clicking survive
      Array.prototype.forEach.call(sortPanel.querySelectorAll('.pcombo__opt'), function (b) {
        var on = !!curField && b.getAttribute('data-plp-field') === curField.field;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
    // a field with only one direction has nothing to reverse
    var twoWay = !!(curField && curField.asc && curField.desc);
    if (dirBtn) {
      dirBtn.disabled = !twoWay;
      var descending = cur.dir === 'desc';
      dirBtn.classList.toggle('is-desc', descending);
      var name = descending ? 'Sort ascending' : 'Sort descending';
      dirBtn.setAttribute('aria-label', name);
      dirBtn.setAttribute('title', name);
    }
    if (sortUI) sortUI.hidden = false;
    bar.classList.add('is-adopted');
  }

  function chooseField(field) {
    var f = fields.filter(function (x) { return x.field === field; })[0];
    if (!f || !source) return;
    var cur = decompose(source.norm(source.current));
    // Keep the direction you were already reading in when the new field has
    // it; otherwise fall to the direction that field actually MEANS. "Date
    // added" ascending is oldest-first, which nobody picks on purpose, and the
    // same goes for a least-selling sort.
    var natural = DEFAULT_DESC[f.field] ? (f.desc || f.asc) : (f.asc || f.desc);
    // ...but a field whose ascending order is the one nobody means ignores the
    // carry-over entirely. Coming from Price ascending, "Date added" was
    // inheriting asc and landing on the OLDEST products in the catalogue.
    // The direction button still flips it deliberately.
    var want = (cur.dir && !DEFAULT_DESC[f.field])
      ? ((cur.dir === 'desc' && f.desc) ? f.desc : (cur.dir === 'asc' && f.asc) ? f.asc : natural)
      : natural;
    if (want != null && want !== source.current) { source.apply(want); syncSort(); }
  }

  function flipDirection() {
    if (!source) return;
    var cur = decompose(source.norm(source.current));
    var f = fields.filter(function (x) { return x.field === cur.field; })[0];
    if (!f || !f.asc || !f.desc) return;
    source.apply(cur.dir === 'desc' ? f.asc : f.desc);
    // resync immediately rather than waiting for the engine: the island
    // re-renders (and the observer below catches it), but the SSR select just
    // changes value and navigates, which mutates no attribute at all — the bar
    // would otherwise show the previous field until the new document painted.
    syncSort();
  }

  if (sortCombo) {
    var closeSort = function () {
      if (!sortPanel) return;
      sortPanel.hidden = true;
      sortCombo.classList.remove('is-open');
      if (sortBtn) sortBtn.setAttribute('aria-expanded', 'false');
    };
    sortCombo.addEventListener('click', function (e) {
      if (e.target.closest('.pcombo__btn')) {
        var open = sortPanel.hidden;
        sortPanel.hidden = !open;
        sortCombo.classList.toggle('is-open', open);
        sortBtn.setAttribute('aria-expanded', String(open));
        return;
      }
      var opt = e.target.closest('[data-plp-field]');
      if (opt) { chooseField(opt.dataset.plpField); closeSort(); }
    });
    document.addEventListener('pointerdown', function (e) { if (!sortCombo.contains(e.target)) closeSort(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && sortPanel && !sortPanel.hidden) { closeSort(); e.stopPropagation(); } }, true);
  }
  if (dirBtn) dirBtn.addEventListener('click', flipDirection);

  /* ---------- view mode ---------- */

  var viewsWrap = bar.querySelector('[data-plp-views]');
  var GRID_SIZES = bar.getAttribute('data-sizes-grid') || '';
  var LIST_SIZES = bar.getAttribute('data-sizes-list') || '';

  // `sizes` cannot see a class, so the view switch has to restate it. Both
  // grids emit the same .card__media contract, so one sweep covers SSR tiles
  // and anything either client engine injected.
  function applySizes(view) {
    if (!GRID_SIZES || !LIST_SIZES) return;
    var want = view === 'list' ? LIST_SIZES : GRID_SIZES;
    var imgs = document.querySelectorAll('.product-grid .card__media img[sizes]');
    for (var i = 0; i < imgs.length; i++) if (imgs[i].sizes !== want) imgs[i].sizes = want;
  }

  function setView(view, persist) {
    view = view === 'list' ? 'list' : 'grid';
    document.documentElement.setAttribute('data-plp-view', view);
    if (viewsWrap) {
      Array.prototype.forEach.call(viewsWrap.querySelectorAll('[data-plp-view]'), function (b) {
        b.setAttribute('aria-pressed', String(b.dataset.plpView === view));
      });
    }
    applySizes(view);
    if (persist) store.set(LS_VIEW, view);
  }

  if (viewsWrap) {
    viewsWrap.hidden = false;
    viewsWrap.addEventListener('click', function (e) {
      var b = e.target.closest('[data-plp-view]');
      if (b) setView(b.dataset.plpView, true);
    });
  }
  setView(store.get(LS_VIEW, 'grid'), false);

  /* ---------- page size ---------- */

  var perUI = bar.querySelector('[data-plp-per-ui]');
  var perCombo = bar.querySelector('[data-plp-per-combo]');
  var perPanel = perCombo && perCombo.querySelector('.pcombo__panel');
  var perBtn = perCombo && perCombo.querySelector('.pcombo__btn');
  var perCurrent = bar.querySelector('[data-plp-per-current]');

  function currentPer() {
    var n = parseInt(store.get(LS_PER, '24'), 10);
    return PER_OPTIONS.indexOf(n) > -1 ? n : 24;
  }

  function renderPer() {
    if (!perPanel) return;
    var n = currentPer();
    if (perCurrent) perCurrent.textContent = String(n);
    perPanel.innerHTML = PER_OPTIONS.map(function (v) {
      var on = v === n;
      return '<button type="button" class="pcombo__opt' + (on ? ' is-active' : '') + '" role="option"'
        + ' aria-selected="' + (on ? 'true' : 'false') + '" data-plp-per="' + v + '">'
        + '<span class="pcombo__name">' + v + '</span>'
        + '<span class="pcombo__tick" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span></button>';
    }).join('');
  }

  function setPer(n, persist) {
    if (PER_OPTIONS.indexOf(n) < 0) return;
    if (persist) store.set(LS_PER, String(n));
    renderPer();
    // typesense-plp.js owns the live query; it listens for this and sets
    // hitsPerPage on the helper. Nothing to do when the island is not running,
    // which is exactly when the control is not rendered.
    document.dispatchEvent(new CustomEvent('plp:per', { detail: n }));
  }

  if (perUI && perCombo) {
    perUI.hidden = false;
    renderPer();
    var closePer = function () {
      perPanel.hidden = true;
      perCombo.classList.remove('is-open');
      if (perBtn) perBtn.setAttribute('aria-expanded', 'false');
    };
    perCombo.addEventListener('click', function (e) {
      if (e.target.closest('.pcombo__btn')) {
        var open = perPanel.hidden;
        perPanel.hidden = !open;
        perCombo.classList.toggle('is-open', open);
        perBtn.setAttribute('aria-expanded', String(open));
        return;
      }
      var opt = e.target.closest('[data-plp-per]');
      if (opt) { setPer(parseInt(opt.dataset.plpPer, 10), true); closePer(); }
    });
    document.addEventListener('pointerdown', function (e) { if (!perCombo.contains(e.target)) closePer(); });
    // announce the stored size once the island is listening
    document.addEventListener('plp:ready', function () { setPer(currentPer(), false); });
    setPer(currentPer(), false);
  }

  /* ---------- keep in step with whatever re-renders under us ---------- */

  syncSort();
  // the island's sort widget mounts asynchronously and re-renders on every
  // refine; the SSR section is replaced wholesale by section-render.js. Both
  // land as subtree mutations under the toolbar's own source slot.
  var srcSlot = bar.querySelector('[data-plp-sort-source]');
  if (srcSlot && 'MutationObserver' in window) {
    var t = 0;
    new MutationObserver(function () { clearTimeout(t); t = setTimeout(syncSort, 60); })
      .observe(srcSlot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-sort-value'] });
  }
  /* ---------- merchandising: keep full-row banners on a row boundary -------
   * Promos splice at an ABSOLUTE product slot (see splicePromos in
   * typesense-plp.js) and a banner is `grid-column: 1 / -1`. If its slot is not
   * a multiple of the current column count the banner cannot fit in the
   * part-filled row it lands in, so the browser pushes it to the next row and
   * leaves the tail of that row EMPTY — a hole in the grid whose size varies
   * with the viewport.
   *
   * The column count is responsive (4 up to 8 across the breakpoints), so this
   * cannot be fixed at splice time on the server: it has to be re-evaluated
   * whenever the grid re-renders or the viewport changes. Move the banner to
   * the nearest row boundary instead — it never reorders products relative to
   * each other, so a sorted grid stays sorted. */
  var balancing = false;
  /* EVERY grid that holds a banner, not "the first .product-grid". Since
   * 52a2d49 (2026-09-17, search dialog renders the catalogue card) the header's
   * search dialog ships `<ol class="ais-Hits-list product-grid sdialog__cards">`
   * ABOVE the PLP in the document, so document.querySelector('.product-grid')
   * returned that empty dialog list, found no banner in it and returned — and
   * the PLP banner was never balanced again. Measured on production
   * /collections/twin-showers at 5 columns (2026-09-24): banner at child
   * index 9, a one-cell hole after the 8th product, the last row of page 1
   * holding one card. */
  function balanceBanners() {
    if (balancing) return;
    var banners = document.querySelectorAll('.product-grid > .promo--banner, .product-grid > .plp-promo-cell--banner, .product-grid > * > .promo--banner');
    var grids = [];
    Array.prototype.forEach.call(banners, function (b) {
      var g = b.closest('.product-grid');
      if (g && grids.indexOf(g) < 0) grids.push(g);
    });
    grids.forEach(balanceGrid);
  }
  function balanceGrid(grid) {
    if (!grid || !grid.children.length) return;
    // a display:none grid (the island's hidden standby list) reports its
    // SPECIFIED tracks, "repeat(4, minmax(0px, 1fr))", which splits into a
    // wrong column count; it has no layout to balance anyway
    if (!grid.getClientRects().length) return;
    var tracks = getComputedStyle(grid).gridTemplateColumns;
    // list view sets display:block, so there are no tracks to balance against
    if (!tracks || tracks === 'none') return;
    var cols = tracks.split(' ').filter(Boolean).length;
    if (cols < 2) return;
    var isBanner = function (el) {
      return el.classList.contains('plp-promo-cell--banner')
        || el.classList.contains('promo--banner')
        || !!el.querySelector('.promo--banner');
    };
    var banners = Array.prototype.filter.call(grid.children, isBanner);
    if (!banners.length) return;
    // NOTE: this flag does NOT stop the observer re-entering. MutationObserver
    // callbacks are microtasks and cannot run while this synchronous function is
    // mid-stack, so no callback ever sees it true. What actually terminates the
    // recursion is CONVERGENCE: insertBefore queues a record, the observer runs
    // again a microtask later, finds idx % cols === 0 and returns without
    // mutating. The flag only guards a synchronous re-entry (a custom element's
    // connectedCallback firing off insertBefore) — impossible with today's
    // <li>/<article> children, kept as a cheap guard against that changing.
    // try/finally so a throw cannot leave it stuck true and silently disable
    // balancing for the rest of the page's life.
    balancing = true;
    try {
    banners.forEach(function (el) {
      var kids = Array.prototype.slice.call(grid.children);
      if (kids.indexOf(el) < 0) return;
      // count CELLS (products + tiles) before the banner. Another banner is
      // a full row, not a cell: counting it (as the index did) put a second
      // banner off the boundary by one.
      var cells = kids.filter(function (k) { return !isBanner(k); });
      var before = 0;
      for (var i = 0; i < kids.length && kids[i] !== el; i++) if (!isBanner(kids[i])) before++;
      // A placed band (assets/plp-merch.js) carries the cell count it was
      // PLANNED after. Snap from that, not from where the last snap left it:
      // re-snapping from the current spot on every resize walks the band a
      // row further each time the column count changes.
      var tagged = el.hasAttribute('data-merch-after') ? el : el.querySelector('[data-merch-after]');
      var target = tagged ? parseInt(tagged.getAttribute('data-merch-after'), 10) : NaN;
      if (!isFinite(target)) target = before;
      var snapped = Math.round(target / cols) * cols;
      // never above the first row or below the last full one: a band is
      // BETWEEN rows of products
      if (snapped < cols) snapped = cols;
      var whole = cells.length - (cells.length % cols);
      if (snapped > whole) snapped = whole;
      if (snapped >= cells.length) snapped = whole > cols ? whole - cols : whole;
      if (snapped < 0) snapped = 0;
      if (snapped === before) return;                   // already there
      grid.insertBefore(el, cells[snapped] || null);
    });
    } finally { balancing = false; }
  }

  document.addEventListener('section:loaded', function () {
    balanceBanners();
    setTimeout(function () { syncSort(); applySizes(document.documentElement.getAttribute('data-plp-view')); }, 0);
  });
  // fresh tiles from either client grid need the current view's sizes
  if ('MutationObserver' in window) {
    var gt = 0;
    new MutationObserver(function (recs) {
      for (var i = 0; i < recs.length; i++) if (recs[i].addedNodes.length) {
        clearTimeout(gt);
        // Balance SYNCHRONOUSLY. MutationObserver callbacks run as a microtask,
        // before the browser paints, so moving the banner here is invisible.
        // Behind the old 120ms debounce it was ~7 painted frames of the banner
        // sitting a cell out of place and then jumping — measured on the live
        // preview: index 9 at 0/40/80ms, index 8 from 120ms. Every tile after
        // it shifted, which read as the grid re-arranging itself after load.
        balanceBanners();
        // sizes are an attribute swap with no layout consequence; still debounced
        gt = setTimeout(function () { applySizes(document.documentElement.getAttribute('data-plp-view')); }, 120);
        return;
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  var rz = 0;
  window.addEventListener('resize', function () { clearTimeout(rz); rz = setTimeout(balanceBanners, 150); });
  balanceBanners();
  }
})();
